import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Search, Users, TrendingUp, AlertTriangle, ShieldAlert,
  ChevronRight, Activity,
} from 'lucide-react';
import { accountsApi, type AccountRecord } from '../../../api/accounts';

// ─── Stat Card ──────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, variant }: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  variant: 'blue' | 'emerald' | 'amber' | 'red';
}) {
  const bg = { blue: 'bg-blue-50 border-blue-100', emerald: 'bg-emerald-50 border-emerald-100', amber: 'bg-amber-50 border-amber-100', red: 'bg-red-50 border-red-100' };
  const iconColor = { blue: 'text-blue-600', emerald: 'text-emerald-600', amber: 'text-amber-600', red: 'text-red-600' };
  return (
    <div className={`rounded-xl border p-4 ${bg[variant]}`}>
      <div className={`${iconColor[variant]} mb-2`}>{icon}</div>
      <p className="text-2xl font-bold text-neutral-900 tabular-nums">{value}</p>
      <p className="text-xs text-neutral-500 mt-0.5">{label}</p>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function riskBadge(level: AccountRecord['risk_level']) {
  const styles = {
    healthy: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20',
    at_risk: 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20',
    critical: 'bg-red-50 text-red-700 ring-1 ring-red-600/20',
  };
  const labels = { healthy: 'Healthy', at_risk: 'At Risk', critical: 'Critical' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[level]}`}>
      {labels[level]}
    </span>
  );
}

function healthBar(score: number) {
  const color = score >= 70 ? 'bg-emerald-500' : score >= 40 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = score >= 70 ? 'text-emerald-700' : score >= 40 ? 'text-amber-700' : 'text-red-700';
  return (
    <div className="flex items-center gap-2">
      <span className={`text-sm font-semibold tabular-nums ${textColor}`}>{score}</span>
      <div className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden max-w-[60px]">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function TableSkeleton() {
  return (
    <div className="p-5 space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 animate-pulse">
          <div className="w-8 h-8 bg-neutral-100 rounded-lg" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 w-32 bg-neutral-100 rounded" />
            <div className="h-3 w-24 bg-neutral-50 rounded" />
          </div>
          <div className="h-3.5 w-12 bg-neutral-100 rounded" />
          <div className="h-5 w-16 bg-neutral-100 rounded-full" />
          <div className="h-3.5 w-10 bg-neutral-100 rounded" />
        </div>
      ))}
    </div>
  );
}

// ─── Empty State ────────────────────────────────────────────────────────────

function EmptyState({ search }: { search: string }) {
  return (
    <div className="py-16 text-center">
      <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-neutral-100 flex items-center justify-center">
        <Building2 size={24} className="text-neutral-300" />
      </div>
      <p className="text-sm font-medium text-neutral-500 mb-1">
        {search ? 'No customers match your search' : 'No customers found'}
      </p>
      <p className="text-xs text-neutral-400">
        {search ? 'Try a different search term' : 'Run WorkOS sync to populate customer data'}
      </p>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function CustomersPage() {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['crm-accounts', search],
    queryFn: () => accountsApi.list({ search: search || undefined, limit: 100 }),
    staleTime: 60_000,
  });

  const accounts = data?.data ?? [];
  const healthy = accounts.filter((a) => a.risk_level === 'healthy').length;
  const atRisk = accounts.filter((a) => a.risk_level === 'at_risk').length;
  const critical = accounts.filter((a) => a.risk_level === 'critical').length;
  const hasPosthog = accounts.some(a => (a.pageviews_30d ?? 0) > 0);

  return (
    <div className="flex flex-col h-full bg-neutral-50">
      <div className="max-w-7xl mx-auto w-full px-6 py-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Building2 size={16} className="text-blue-600" />
              </div>
              <h1 className="text-xl font-semibold text-neutral-900">Customers</h1>
            </div>
            <p className="text-sm text-neutral-500 ml-[42px]">Customer organizations linked via WorkOS</p>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total Customers" value={data?.total ?? accounts.length} icon={<Building2 size={18} />} variant="blue" />
          <StatCard label="Healthy" value={healthy} icon={<TrendingUp size={18} />} variant="emerald" />
          <StatCard label="At Risk" value={atRisk} icon={<AlertTriangle size={18} />} variant="amber" />
          <StatCard label="Critical" value={critical} icon={<ShieldAlert size={18} />} variant="red" />
        </div>

        {/* Search */}
        <div className="relative max-w-sm mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customers..."
            className="w-full pl-9 pr-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white placeholder:text-neutral-400 outline-none focus:ring-1 focus:ring-coral-500 focus:border-transparent transition-shadow"
          />
        </div>

        {/* Table */}
        <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <TableSkeleton />
          ) : isError ? (
            <div className="p-10 text-center">
              <p className="text-sm text-red-600">Failed to load customers. Check that the CRM service is running.</p>
            </div>
          ) : accounts.length === 0 ? (
            <EmptyState search={search} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 bg-neutral-50/80">
                    <th className="text-left px-5 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Customer</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Domain</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Health</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Risk</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Active Users</th>
                    {hasPosthog && (
                      <th className="text-center px-4 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        <span className="flex items-center justify-center gap-1"><Activity size={12} /> Views 30d</span>
                      </th>
                    )}
                    <th className="text-center px-4 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider">Tickets</th>
                    <th className="w-8 px-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50">
                  {accounts.map((account) => (
                    <tr
                      key={account.id}
                      onClick={() => navigate(account.id)}
                      className="group hover:bg-neutral-50/80 transition-colors cursor-pointer"
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center text-neutral-500 text-xs font-semibold flex-shrink-0">
                            {account.name.slice(0, 2).toUpperCase()}
                          </div>
                          <span className="font-medium text-neutral-900">{account.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-neutral-500">{account.domain ?? '-'}</td>
                      <td className="px-4 py-3.5">{healthBar(account.health_score)}</td>
                      <td className="px-4 py-3.5 text-center">{riskBadge(account.risk_level)}</td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="inline-flex items-center gap-1 text-neutral-600">
                          <Users size={13} className="text-neutral-400" />
                          <span className="tabular-nums">{account.active_members}/{account.member_count}</span>
                        </span>
                      </td>
                      {hasPosthog && (
                        <td className="px-4 py-3.5 text-center text-neutral-600 tabular-nums">
                          {(account.pageviews_30d ?? 0).toLocaleString()}
                        </td>
                      )}
                      <td className="px-4 py-3.5 text-center">
                        {(account.open_tickets ?? 0) > 0 ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                            {account.open_tickets}
                          </span>
                        ) : (
                          <span className="text-neutral-300">0</span>
                        )}
                      </td>
                      <td className="px-2 py-3.5">
                        <ChevronRight size={14} className="text-neutral-300 group-hover:text-neutral-500 transition-colors" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
